import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Map from '../components/Map';
import POIPopup from '../components/POIPopup';
import POIForm from '../components/POIForm';
import CardForm from '../components/CardForm';
import LocationControl from '../components/LocationControl';
import AddressSearch from '../components/AddressSearch';
import StickyCreatorCards from '../components/StickyCreatorCards';
import { poiService, POI, Card, City, CreatePOIRequest, CreateCardRequest } from '../services/poiService';
import { deletePoi } from '../services/api';
import { cardCacheService } from '../services/cardCacheService';
import { useGeolocation } from '../hooks/useGeolocation';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const MapExplore: React.FC = () => {
  const { user } = useAuth();
  const { selectedLanguage } = useLanguage();
  const navigate = useNavigate();
  const hasInitiallyCentered = useRef(false);
  const locationRequestedRef = useRef(false);
  const initialPOIsFetchedRef = useRef(false);
  const [pois, setPois] = useState<POI[]>([]);
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [selectedPOICards, setSelectedPOICards] = useState<Card[]>([]);
  const [isLoadingPOIs, setIsLoadingPOIs] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<{ current: number; total: number } | null>(null);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [centerOnUser, setCenterOnUser] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([40.7128, -74.0060]);
  const [waitingForLocation, setWaitingForLocation] = useState(false);
  const [showMap, setShowMap] = useState(false);
  
  // Cities state for showing city markers when zoomed out
  const [cities, setCities] = useState<City[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const citiesFetchedRef = useRef(false);
  
  // New states for POI and Card creation
  const [newPOILocation, setNewPOILocation] = useState<[number, number] | null>(null);
  const [showPOIForm, setShowPOIForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [selectedPOIForCard, setSelectedPOIForCard] = useState<POI | null>(null);
  const [isCreatingPOI, setIsCreatingPOI] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);
  const [showFullPOIPopup, setShowFullPOIPopup] = useState(false);
  
  // Card editing state
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [editingCardPOI, setEditingCardPOI] = useState<POI | null>(null);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [isDuplicatingCard, setIsDuplicatingCard] = useState(false);

  // Address search state
  const [searchCenter, setSearchCenter] = useState<[number, number] | null>(null);

  // Geolocation hook
  const {
    latitude,
    longitude,
    accuracy,
    error: locationError,
    loading: locationLoading,
    supported: locationSupported,
    requestLocation,
    stopTracking,
    isTracking
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 300000, // Increase to 5 minutes to reduce frequent updates
    watch: false // Disable automatic watching to prevent continuous updates
  });

  // Create user location object
  const userLocation = latitude && longitude ? {
    latitude,
    longitude,
    accuracy: accuracy || undefined
  } : null;

  // Update map center when user location is first obtained
  useEffect(() => {
    if (latitude && longitude && !hasInitiallyCentered.current) {
      setMapCenter([latitude, longitude]);
      setCenterOnUser(true);
      hasInitiallyCentered.current = true;
      // Reset the flag after a short delay
      setTimeout(() => setCenterOnUser(false), 500);
    }
  }, [latitude, longitude]);

  // Request location when component mounts and handle initial loading state
  useEffect(() => {
    if (locationSupported && !locationRequestedRef.current) {
      locationRequestedRef.current = true;
      setWaitingForLocation(true);
      requestLocation();
    } else if (!locationSupported) {
      // Location not supported, show map immediately
      setShowMap(true);
    }
  }, [locationSupported, requestLocation]);

  // Handle location result - either success or error
  useEffect(() => {
    // If we're waiting for location and we get either a location or an error
    if (waitingForLocation && (latitude !== null || locationError !== null)) {
      setWaitingForLocation(false);
      setShowMap(true);
    }
  }, [waitingForLocation, latitude, locationError]);

  // Fetch POIs only once after location is determined or when map is shown
  useEffect(() => {
    if (showMap && !initialPOIsFetchedRef.current) {
      initialPOIsFetchedRef.current = true;
      fetchPOIs(latitude || undefined, longitude || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  // Reusable function to fetch POIs for a given location
  const fetchPOIs = async (userLat?: number, userLng?: number, centerLat?: number, centerLng?: number) => {
    try {
      setIsLoadingPOIs(true);
      setError(null);
      setLoadingProgress(null);
      
      let allPois: POI[] = [];
      let offset = 0;
      const limit = 100; // Use maximum allowed limit
      let hasMore = true;
      let totalPOIs = 0;

      // Priority: explicit coordinates > user location > map center
      let effectiveLat: number;
      let effectiveLng: number;
      let locationSource: string;

      if (centerLat !== undefined && centerLng !== undefined) {
        effectiveLat = centerLat;
        effectiveLng = centerLng;
        locationSource = 'map center';
      } else if (userLat !== undefined && userLng !== undefined) {
        effectiveLat = userLat;
        effectiveLng = userLng;
        locationSource = 'user location';
      } else {
        effectiveLat = mapCenter[0];
        effectiveLng = mapCenter[1];
        locationSource = 'default location';
      }

      const radiusMeters = 50000; // 50km radius for POI fetching

      console.log(`Fetching POIs within ${radiusMeters/1000}km of ${locationSource} (${effectiveLat}, ${effectiveLng})`);

      // Always fetch POIs with location filtering for performance and relevance
      while (hasMore) {
        const queryParams = { 
          latitude: effectiveLat, 
          longitude: effectiveLng, 
          radius: radiusMeters, 
          limit, 
          offset 
        };

        const response = await poiService.fetchPOIs(queryParams);
        allPois = [...allPois, ...response.data];
        hasMore = response.pagination.has_more;
        totalPOIs = response.pagination.total;
        offset += limit;
        
        // Update loading progress
        setLoadingProgress({ current: allPois.length, total: totalPOIs });
        
        // Optional: Add a small delay to avoid overwhelming the server
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`Loaded ${allPois.length} POIs total with location filtering (${locationSource})`);
      setPois(allPois);
    } catch (err) {
      setError('Failed to load points of interest. Please try again later.');
      console.error('Error fetching POIs:', err);
    } finally {
      setIsLoadingPOIs(false);
      setLoadingProgress(null);
    }
  };

  // Function to fetch cities
  const fetchCities = async () => {
    try {
      setIsLoadingCities(true);
      console.log('Fetching cities for zoomed-out view...');
      
      const response = await poiService.fetchCities();
      console.log(`Loaded ${response.data.length} cities`);
      setCities(response.data);
      citiesFetchedRef.current = true;
    } catch (err) {
      console.error('Error fetching cities:', err);
      // Don't set error state for cities, just log it
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Handle zoom changes - fetch cities when zoomed out
  const handleZoomChange = (zoom: number) => {
    // Fetch cities when zoomed out (zoom level < 10) and not already fetched
    if (zoom < 10 && !citiesFetchedRef.current && !isLoadingCities) {
      fetchCities();
    }
    
    // Clear cities when zoomed in (zoom level >= 10)
    if (zoom >= 10 && cities.length > 0) {
      console.log('Zoomed in, hiding city markers');
      setCities([]);
      citiesFetchedRef.current = false;
    }
  };

  // Fetch POIs when component mounts or when location becomes available
  // Note: mapCenter is NOT in dependencies because map center changes are handled by handleMapMove
  // This effect is now removed as POI fetching is handled by the showMap effect above

  // Handle map movement to fetch new POIs
  const handleMapMove = async (lat: number, lng: number) => {
    // Update map center to reflect the new position
    setMapCenter([lat, lng]);
    
    // Fetch new POIs for the new location
    await fetchPOIs(undefined, undefined, lat, lng);
  };

  // Clear cache when language changes to ensure fresh data
  useEffect(() => {
    cardCacheService.clearAllCache();
  }, [selectedLanguage]);

  // Handle POI marker click - determine if it's for viewing or adding card
  const handlePOIClick = async (poi: POI) => {
    // If user is not authenticated, navigate to POILandingPage
    if (!user) {
      navigate(`/app/${poi.id}`);
      return;
    }
    
    // For authenticated users, show the popup with buttons
    setSelectedPOI(poi);
    setShowFullPOIPopup(false); // Just show popup, not full modal
  };
  
  // Handle "View Details" button click
  const handleViewDetails = async (poi: POI) => {
    setSelectedPOI(poi);
    setShowFullPOIPopup(true);
    
    // Check cache first
    const cachedCards = cardCacheService.getCachedCards(poi.id);
    if (cachedCards) {
      setSelectedPOICards(cachedCards);
      setIsLoadingCards(false);
      return;
    }

    // Check if already loading
    if (cardCacheService.isLoading(poi.id)) {
      setIsLoadingCards(true);
      return;
    }

    setIsLoadingCards(true);
    setSelectedPOICards([]);
    cardCacheService.setLoading(poi.id, true);

    try {
      const cardsResponse = await poiService.fetchCardsByPOI(poi.id, selectedLanguage?.code);
      const cards = cardsResponse.data;
      
      // Cache the results
      cardCacheService.setCachedCards(poi.id, cards);
      setSelectedPOICards(cards);
    } catch (err) {
      console.error(`Error fetching cards for POI ${poi.id}:`, err);
      setSelectedPOICards([]);
    } finally {
      setIsLoadingCards(false);
      cardCacheService.setLoading(poi.id, false);
    }
  };
  
  // Handle "Add Card" button click
  const handleAddCard = (poi: POI) => {
    // Check if user is authenticated
    if (!user) {
      alert('You must be logged in to add cards. Please sign in or create an account.');
      return;
    }
    
    setSelectedPOIForCard(poi);
    setShowCardForm(true);
    setSelectedPOI(null); // Close POI popup
  };

  // Handle card edit button click
  const handleCardEdit = async (card: Card) => {
    try {
      // Fetch the original card data (no translation) to ensure we're editing the source content
      const response = await poiService.fetchCardById(card.id_v2);
      setEditingCard(response.data);
      setEditingCardPOI(selectedPOI); // Store the POI for cache invalidation
      setShowCardForm(true);
      setSelectedPOI(null); // Close POI popup
      setShowFullPOIPopup(false);
    } catch (error) {
      console.error('Error fetching original card data for editing:', error);
      // Fallback to using the existing card data if fetch fails
      setEditingCard(card);
      setEditingCardPOI(selectedPOI); // Store the POI for cache invalidation
      setShowCardForm(true);
      setSelectedPOI(null); // Close POI popup
      setShowFullPOIPopup(false);
    }
  };

  // Close POI popup
  const handleClosePopup = () => {
    setSelectedPOI(null);
    setSelectedPOICards([]);
    setIsLoadingCards(false);
    setShowFullPOIPopup(false);
  };

  // Handle card delete
  const handleCardDelete = async (cardIdV2: string) => {
    try {
      await poiService.deleteCard(cardIdV2);

      // Remove the card from the current cards list
      setSelectedPOICards(prevCards => 
        prevCards.filter(card => card.id_v2 !== cardIdV2)
      );

      // Update cache if POI is selected
      if (selectedPOI) {
        const updatedCards = selectedPOICards.filter(card => card.id_v2 !== cardIdV2);
        cardCacheService.setCachedCards(selectedPOI.id, updatedCards);
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      throw error;
    }
  };

  // Handle POI edit
  const handlePOIEdit = (updatedPOI: POI) => {
    // Update the POI in the list
    setPois(prevPois => 
      prevPois.map(poi => poi.id === updatedPOI.id ? updatedPOI : poi)
    );
    
    // Update the selected POI
    setSelectedPOI(updatedPOI);
  };

  // Handle POI delete (admin only)
  const handlePOIDelete = async (poiIdV2: string) => {
    try {
      await deletePoi(poiIdV2);

      // Remove the POI from the pois list
      setPois(prevPois => prevPois.filter(poi => poi.id !== poiIdV2));

      // Clear the selected POI if it was the deleted one
      if (selectedPOI?.id === poiIdV2) {
        setSelectedPOI(null);
        setSelectedPOICards([]);
      }

      // Clear cache for this POI
      cardCacheService.clearCachedCards(poiIdV2);
    } catch (error) {
      console.error('Error deleting POI:', error);
      throw error;
    }
  };

  // Handle center on user location
  const handleCenterOnUser = async () => {
    if (userLocation) {
      setMapCenter([userLocation.latitude, userLocation.longitude]);
      setCenterOnUser(true);
      // Reset the flag after a short delay
      setTimeout(() => setCenterOnUser(false), 100);
    } else if (locationSupported) {
      // Request fresh location if we don't have one
      try {
        await requestLocation();
      } catch (error) {
        console.error('Failed to get location:', error);
      }
    }
  };

  // Handle city marker click - zoom in to city coordinates
  const handleCityClick = async (city: City) => {
    console.log(`Zooming in to city: ${city.city}, ${city.country}`);
    setMapCenter([city.latitude, city.longitude]);
    setSearchCenter([city.latitude, city.longitude]);
    // Reset search center flag after a short delay
    setTimeout(() => setSearchCenter(null), 500);
    
    // Fetch POIs for the city location to ensure POIs are loaded for this city
    // Note: MapMoveHandler may also trigger a fetch if distance > 5km, but this is acceptable
    await fetchPOIs(undefined, undefined, city.latitude, city.longitude);
  };
  
  // Handle map click for new POI
  const handleMapClick = (lat: number, lng: number) => {
    // Check if user is authenticated
    if (!user) {
      // Don't show alert on map click as it would be too intrusive
      // Instead, we'll prevent POI creation
      return;
    }
    
    setNewPOILocation([lat, lng]);
    setSelectedPOI(null); // Close any open popups
    setShowCardForm(false);
    setShowPOIForm(false);
  };
  
  // Handle new POI confirmation
  const handleNewPOIConfirm = () => {
    // Double-check authentication (should not be needed due to handleMapClick check, but for safety)
    if (!user) {
      alert('You must be logged in to create POIs. Please sign in or create an account.');
      setNewPOILocation(null);
      return;
    }
    
    if (newPOILocation) {
      setShowPOIForm(true);
    }
  };
  
  // Handle new POI cancellation
  const handleNewPOICancel = () => {
    setNewPOILocation(null);
  };
  
  // Handle POI form submission
  const handlePOISubmit = async (data: CreatePOIRequest) => {
    setIsCreatingPOI(true);
    try {
      const response = await poiService.createPOI(data);
      const newPOI = response.data;
      
      // Add the new POI to the list
      setPois(prev => [...prev, newPOI]);
      
      // Clear form state
      setShowPOIForm(false);
      setNewPOILocation(null);
      
      // Show success message (you could add a toast here)
      console.log('POI created successfully:', newPOI);
      
    } catch (err) {
      console.error('Error creating POI:', err);
      // You could show an error toast here
    } finally {
      setIsCreatingPOI(false);
    }
  };
  
  // Handle POI form cancellation
  const handlePOICancel = () => {
    setShowPOIForm(false);
    setNewPOILocation(null);
  };
  
  // Handle Card form submission
  const handleCardSubmit = async (data: CreateCardRequest) => {
    if (editingCard) {
      // Edit mode
      setIsEditingCard(true);
      try {
        const response = await poiService.updateCard(editingCard.id_v2, {
          category: data.category,
          title: data.title,
          subtitle: data.subtitle,
          text: data.text,
          audio_url: data.audio_url,
          video_url: data.video_url,
          image_url: data.image_url,
          language_code: data.language_code
        });

        // Update the card in the current cards list if we have selected cards
        setSelectedPOICards(prevCards => 
          prevCards.map(card => 
            card.id_v2 === editingCard.id_v2 ? response.data : card
          )
        );

        // Clear cache to ensure fresh data on next load - use stored POI UUID
        if (editingCardPOI) {
          cardCacheService.clearCachedCards(editingCardPOI.id);
        }

        // Clear form state
        setShowCardForm(false);
        setEditingCard(null);
        setEditingCardPOI(null);
        
        console.log('Card updated successfully:', response.data);
        
      } catch (err) {
        console.error('Error updating Card:', err);
        alert('Failed to update card. Please try again.');
      } finally {
        setIsEditingCard(false);
      }
    } else {
      // Create mode
      setIsCreatingCard(true);
      try {
        const response = await poiService.createCard(data);
        const newCard = response.data;
        
        // Clear cache for this POI so cards are refreshed
        cardCacheService.clearCachedCards(data.poi_id);
        
        // Clear form state
        setShowCardForm(false);
        setSelectedPOIForCard(null);
        
        // Show success message (you could add a toast here)
        console.log('Card created successfully:', newCard);
        
      } catch (err) {
        console.error('Error creating Card:', err);
        // You could show an error toast here
      } finally {
        setIsCreatingCard(false);
      }
    }
  };
  
  // Handle Card form cancellation
  const handleCardCancel = () => {
    setShowCardForm(false);
    setSelectedPOIForCard(null);
    setEditingCard(null);
    setEditingCardPOI(null);
  };

  // Handle Card duplication (create a copy of the current card)
  const handleDuplicateCard = async (data: CreateCardRequest) => {
    if (!editingCardPOI) {
      alert('Cannot duplicate card: POI information is missing.');
      return;
    }
    setIsDuplicatingCard(true);
    try {
      // Card.poi_id is an integer FK; use the POI's UUID for the create request
      const response = await poiService.createCard({ ...data, poi_id: editingCardPOI.id });
      const newCard = response.data;

      // Clear cache so the duplicated card appears on next load
      cardCacheService.clearCachedCards(editingCardPOI.id);

      // Close the form
      setShowCardForm(false);
      setEditingCard(null);
      setEditingCardPOI(null);

      console.log('Card duplicated successfully:', newCard);
    } catch (err) {
      console.error('Error duplicating Card:', err);
      alert('Failed to duplicate card. Please try again.');
    } finally {
      setIsDuplicatingCard(false);
    }
  };

  // Handle address search selection
  const handleAddressSelect = async (lat: number, lng: number, displayName: string) => {
    console.log('Address selected:', displayName, lat, lng);
    setMapCenter([lat, lng]);
    setSearchCenter([lat, lng]);
    // Reset search center flag after a short delay
    setTimeout(() => setSearchCenter(null), 500);
    
    // Fetch POIs for the searched location
    await fetchPOIs(undefined, undefined, lat, lng);
  };

  // Get cache statistics for debugging
  const cacheStats = cardCacheService.getCacheStats();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800">
      {/* Creator-focused welcome section with sticky cards - only shown when user is NOT logged in */}
      {!user && (
        <>
          <StickyCreatorCards />
          
          {/* Sign up CTA section */}
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-8 sm:py-12">
            <div className="text-center bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 sm:p-8 mb-6 sm:mb-8">
              <p className="text-lg text-gray-800 dark:text-gray-200 mb-4 font-semibold">
                Sign up today to start sharing your stories and help explorers discover the world around them in a new way.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link 
                  to="/signup" 
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                >
                  Sign Up as Creator
                </Link>
                <Link 
                  to="/login" 
                  className="inline-flex items-center justify-center px-6 py-3 border border-primary-600 text-base font-medium rounded-md text-primary-600 bg-white dark:bg-gray-800 hover:bg-primary-50 dark:bg-primary-900/20 transition-colors"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        
        {/* Logged-in user view - simplified header */}
        {user && (
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Blip Places
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 dark:text-gray-500 max-w-2xl mx-auto px-2">
              Discover interesting points of interest around you and blip detailed information cards about each location.
            </p>
          </div>
        )}

        {/* Map Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              Interactive Map
            </h2>
            <div className="flex flex-col sm:flex-row sm:items-center text-sm sm:space-x-4 space-y-1 sm:space-y-0">
              {isLoadingPOIs && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 order-1 sm:order-none">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 dark:border-primary-400 mr-2"></div>
                  <span className="hidden sm:inline">
                    {loadingProgress ? (
                      `Loading POIs... ${loadingProgress.current}/${loadingProgress.total}`
                    ) : (
                      'Loading POIs...'
                    )}
                  </span>
                  <span className="sm:hidden">
                    Loading...
                  </span>
                </div>
              )}
              {/* Cache stats for debugging - hide on mobile */}
              {!import.meta.env.PROD && (
                <div className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500">
                  Cache: {cacheStats.validEntries}/{cacheStats.totalEntries}
                </div>
              )}
            </div>
          </div>

          {/* Address Search */}
          <div className="mb-4">
            <AddressSearch
              onAddressSelect={handleAddressSelect}
              className="mx-auto"
            />
          </div>

          {/* Location Control */}
          <LocationControl
            isTracking={isTracking}
            hasLocation={!!userLocation}
            error={locationError}
            loading={locationLoading}
            supported={locationSupported}
            onRequestLocation={requestLocation}
            onStopTracking={stopTracking}
            onCenterOnUser={handleCenterOnUser}
          />

          {/* Show error message but still display the map */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-red-800">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm text-red-700 underline hover:text-red-800 mt-1"
                  >
                    Try refreshing the page
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Show loading message while waiting for location */}
          {waitingForLocation && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 dark:border-primary-400 mr-3"></div>
                <p className="text-sm text-primary-800">Getting your location...</p>
              </div>
            </div>
          )}

          {/* Show map once location is determined or not supported */}
          {showMap && (
            <Map 
              center={mapCenter}
              zoom={userLocation ? 15 : 13} 
              height="h-64 sm:h-80 md:h-96 lg:h-[600px]"
              className="w-full"
              pois={pois}
              cities={cities}
              userLocation={userLocation}
              centerOnUser={centerOnUser}
              centerOnSearch={searchCenter}
              onPOIClick={handlePOIClick}
              onPOIViewDetails={handleViewDetails}
              onPOIAddCard={handleAddCard}
              onCityClick={handleCityClick}
              onMapClick={handleMapClick}
              onMapMove={handleMapMove}
              onZoomChange={handleZoomChange}
              newPOILocation={newPOILocation}
              onNewPOIConfirm={handleNewPOIConfirm}
              onNewPOICancel={handleNewPOICancel}
              isAuthenticated={!!user}
            />
          )}
        </div>

        {/* Forms Section */}
        {(showPOIForm || showCardForm) && (
          <div className="mt-8">
            {showPOIForm && newPOILocation && (
              <POIForm
                location={{ lat: newPOILocation[0], lng: newPOILocation[1] }}
                onSubmit={handlePOISubmit}
                onCancel={handlePOICancel}
                isLoading={isCreatingPOI}
              />
            )}
            
            {showCardForm && (selectedPOIForCard || editingCard) && (
              <CardForm
                poiId={
                  editingCard 
                    ? editingCard.poi_id.toString()
                    : selectedPOIForCard!.id
                }
                poiName={editingCard ? (editingCard.poi_name || 'Unknown Location') : selectedPOIForCard!.name}
                onSubmit={handleCardSubmit}
                onDuplicate={editingCard ? handleDuplicateCard : undefined}
                onCancel={handleCardCancel}
                isLoading={editingCard ? isEditingCard : isCreatingCard}
                isDuplicating={isDuplicatingCard}
                initialData={editingCard ? {
                  category: editingCard.category,
                  title: editingCard.title || '',
                  subtitle: editingCard.subtitle || '',
                  text: editingCard.text || '',
                  audio_url: editingCard.audio_url || '',
                  video_url: editingCard.video_url || '',
                  image_url: editingCard.image_url || '',
                  language_code: editingCard.language_code
                } : undefined}
              />
            )}
          </div>
        )}
      </div>

      {/* POI Details Popup - only show for full view */}
      {selectedPOI && showFullPOIPopup && (
        <POIPopup
          poi={selectedPOI}
          cards={selectedPOICards}
          isLoading={isLoadingCards}
          onClose={handleClosePopup}
          onCardDelete={handleCardDelete}
          onCardEdit={handleCardEdit}
          onPOIDelete={handlePOIDelete}
          onPOIEdit={handlePOIEdit}
        />
      )}
    </div>
  );
};

export default MapExplore;
